import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { AgGridReact } from 'ag-grid-react';
import type { GridApi } from 'ag-grid-community';
import {
  CtyButton,
  CtyShowToast,
  CtySpinner,
  checkType,
  useHasApplicationResource,
  useAgGridStatePersistence,
  applyPersistedSortToRef,
  getTableState,
  appendAgGridConditionsToApiFilters,
  ServerPaginationPanel,
  serverSideFilterComponents,
  serverSideFilterContext,
  serverSideSortColDef,
  TableColumnChooser,
} from '@circuitry-ai/doc-ui';
import {
  useGetFgaGroupsQuery,
  useLazyGetFgaUserByIdQuery,
  useQueryFgaUsersMutation,
} from "../../../../shared/data/src/features/settings/settingsApiSlice";
import { GenericCellRenderer, ActionButton } from '../components/CellRenderers';
import CreateEditUserModal from '../components/CreateEditUserModal';
import DeleteModal from '../components/DeleteModal';
import { buildUserWithGroups } from '../fga-data-utils';

const USERS_TABLE_ID = 'fga-users-table';
const USERS_PAGE_SIZE = 20;
// The query API allows at most 100 rows per page
const USERS_PAGE_SIZE_OPTIONS = [20, 50, 100];
const SEARCH_DEBOUNCE_MS = 500;
const FILTER_DEBOUNCE_MS = 700;

const textFilterConfig = {
  filter: 'agTextColumnFilter' as const,
  floatingFilter: true,
  suppressFloatingFilterButton: true,
  filterParams: {
    buttons: ['reset'],
    maxNumConditions: 1,
  },
};

/** Maps column-chooser labels to AG Grid colId (the API field name). Actions is always shown. */
const USERS_COLUMN_LABEL_TO_COL_ID: Record<string, string> = {
  Name: 'name',
  Email: 'email',
  'Organization Name': 'organization_name',
  Connection: 'connection',
  Inviter: 'inviter',
  Status: 'status',
  'Assigned Roles': 'assigned_groups',
};
const USERS_COLUMN_LABELS = Object.keys(USERS_COLUMN_LABEL_TO_COL_ID);

type SortState = { field: string | null; order: 'asc' | 'desc' | null };

/** Grid filter model + search box + role chip → query API filters */
function buildUsersApiFilters(filterModel: Record<string, any>, searchTerm: string, selectedGroup: string | null) {
  const filters: Record<string, unknown>[] = [];
  for (const [field, filter] of Object.entries(filterModel)) {
    const parts: Record<string, unknown>[] = [];
    appendAgGridConditionsToApiFilters(parts, field, filter);
    if (field === 'assigned_groups') {
      // A user has a list of roles: exact match means "has this role"
      for (const part of parts) {
        if (part.condition === 'equals' && part[field] !== null) filters.push({ [field]: [part[field]], condition: 'in' });
        else if (part.condition === 'does_not_equal' && part[field] !== null) filters.push({ [field]: [part[field]], condition: 'not_in' });
        else filters.push(part);
      }
    } else {
      filters.push(...parts);
    }
  }
  const search = searchTerm.trim();
  if (search) filters.push({ search });
  if (selectedGroup) filters.push({ assigned_groups: [selectedGroup], condition: 'in' });
  return filters;
}

function useDebouncedCallback<T extends (...args: any[]) => void>(fn: T, delay: number) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  useEffect(() => () => clearTimeout(timerRef.current), []);
  return useCallback((...args: Parameters<T>) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]) as T;
}

// Inline cell renderers for better performance
const UserNameCellRenderer = (params: any) => <GenericCellRenderer type="users" data={params.data} field="name" />;

const AssignedGroupsCellRenderer = (params: any) => {
  const groups = params.data?.groups || [];
  return (
    <div className="flex flex-wrap gap-2 items-center justify-start h-full">
      {groups.map((group: string) => (
        <span key={group} className="inline-flex items-center px-1 py-1 rounded-full text-xs font-medium bg-default-100 text-default-700">
          {group}
        </span>
      ))}
    </div>
  );
};

/** Actions header: column title plus the show/hide columns menu (gridApiRef comes via headerComponentParams) */
const ActionsHeaderWithColumnChooser = (params: any) => (
  <div className="flex items-center justify-between w-full gap-2">
    <span className="ag-header-cell-text">{params.displayName}</span>
    <TableColumnChooser
      triggerId="fga-users-columns-chooser"
      triggerTooltip="hide/show columns"
      columnLabels={USERS_COLUMN_LABELS}
      gridApiRef={params.gridApiRef}
      tableId={USERS_TABLE_ID}
      labelToColId={USERS_COLUMN_LABEL_TO_COL_ID}
    />
  </div>
);

const createActionsCellRenderer = (
  onEdit: (data: any) => void,
  onDelete: (data: any) => void,
  isDeleting: (userId: string) => boolean,
  isDisabled?: boolean
) => (params: any) => (
  <div className="flex gap-2">
    <button
      onClick={() => onEdit(params.data)}
      className="p-2 hover:bg-default-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label={`Edit ${params.data.name} user`}
      disabled={isDisabled}
    >
      <Icon icon="mdi:pencil" className="w-4 h-4 text-default-500" />
    </button>
    <ActionButton
      onPress={() => onDelete(params.data)}
      isLoading={isDeleting(params.data.user_id)}
      isDisabled={isDisabled}
    />
  </div>
);
export const UsersTab: React.FC = () => {
  const hasAccessManagement = useHasApplicationResource('access_management');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const deletingUsers = useMemo(() => new Set<string>(), []);
  const [userModal, setUserModal] = useState({ isOpen: false, mode: 'create' as 'create' | 'edit', userData: null as any, isUserDataLoading: false });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, userData: null as any });

  // The grid holds only the page shown; the server filters, sorts and pages
  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: USERS_PAGE_SIZE, total: 0 });

  // API hooks
  const { data: groupsData } = useGetFgaGroupsQuery(undefined, { refetchOnMountOrArgChange: true });
  const [triggerGetUserById] = useLazyGetFgaUserByIdQuery();
  const [queryFgaUsers] = useQueryFgaUsersMutation();

  const gridApiRef = useRef<GridApi | null>(null);
  const targetPageRef = useRef({ page: 1, pageSize: USERS_PAGE_SIZE });
  const requestIdRef = useRef(0);
  const inFlightRequestKeyRef = useRef<string | null>(null);
  const sortRef = useRef<SortState>({ field: null, order: null });
  const persistedSortAppliedRef = useRef(false);
  if (!persistedSortAppliedRef.current) {
    persistedSortAppliedRef.current = true;
    applyPersistedSortToRef(USERS_TABLE_ID, sortRef);
  }
  const loadPageRef = useRef<(page: number, pageSize: number) => void>(() => undefined);
  const {
    augmentGridReady,
    mergeColumnDefs,
    suppressDragLeaveHidesColumns,
    ignoreRestoreQueryEventsRef,
  } = useAgGridStatePersistence(USERS_TABLE_ID);

  const selectedGroup = selectedGroups[0] ?? null;

  /** One API call per page. The page shown stays on screen under the loader until the response replaces it. */
  loadPageRef.current = (page: number, pageSize: number) => {
    targetPageRef.current = { page, pageSize };
    const api = gridApiRef.current;
    // Before the grid is ready (first load), use the filters it will restore
    const filterModel = (api ? api.getFilterModel() : getTableState(USERS_TABLE_ID)?.filterModel) ?? {};
    const body: any = {
      pagination: { page, per_page: pageSize },
      filters: buildUsersApiFilters(filterModel as Record<string, any>, debouncedSearchTerm, selectedGroup),
    };
    if (sortRef.current.field && sortRef.current.order) {
      body.sorting = { field: sortRef.current.field, order: sortRef.current.order };
    }

    const requestKey = JSON.stringify(body);
    if (inFlightRequestKeyRef.current === requestKey) return; // same request already on its way
    inFlightRequestKeyRef.current = requestKey;
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    queryFgaUsers(body).unwrap()
      .then((response: any) => {
        if (requestId !== requestIdRef.current) return; // replaced by a newer request
        const payload = response?.data ?? response;
        const total = payload?.total ?? 0;
        // A filter or delete can leave fewer pages than the one requested: show the new last page instead
        const lastPage = Math.max(1, Math.ceil(total / pageSize));
        if (page > lastPage) {
          inFlightRequestKeyRef.current = null;
          loadPageRef.current(lastPage, pageSize);
          return;
        }
        setRows((payload?.users ?? []).map(buildUserWithGroups));
        setPagination({ page, pageSize, total });
      })
      .catch((error: any) => {
        if (requestId !== requestIdRef.current) return;
        CtyShowToast(checkType.error, error?.message ?? 'Failed to fetch users');
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        inFlightRequestKeyRef.current = null;
        setIsLoading(false);
      });
  };
  const loadPage = useCallback((page: number, pageSize: number) => loadPageRef.current(page, pageSize), []);

  /** Reload the page being shown (sort changes, after create/edit/delete) */
  const refetchUsers = useCallback(
    () => loadPage(targetPageRef.current.page, targetPageRef.current.pageSize),
    [loadPage]
  );
  const loadFirstPage = useCallback(() => loadPage(1, targetPageRef.current.pageSize), [loadPage]);
  const debouncedRefetchForSort = useDebouncedCallback(refetchUsers, FILTER_DEBOUNCE_MS);
  const debouncedLoadFirstPage = useDebouncedCallback(loadFirstPage, FILTER_DEBOUNCE_MS);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // First load, then back to page 1 whenever the search or role chip changes
  useEffect(() => {
    loadPage(1, targetPageRef.current.pageSize);
  }, [debouncedSearchTerm, selectedGroup, loadPage]);

  const onPageChange = useCallback((page: number) => loadPage(page, targetPageRef.current.pageSize), [loadPage]);

  /** Keep the first row shown on screen when the page size changes */
  const onPageSizeChange = useCallback((pageSize: number) => {
    const firstRowIndex = (pagination.page - 1) * pagination.pageSize;
    loadPage(Math.floor(firstRowIndex / pageSize) + 1, pageSize);
  }, [loadPage, pagination.page, pagination.pageSize]);

  const onSortChanged = useCallback((e: any) => {
    const api: GridApi | null = e.api;
    if (!api || ignoreRestoreQueryEventsRef.current) return;
    const state = api.getColumnState().find((c: any) => !!c.sort);
    sortRef.current = state ? { field: state.colId, order: state.sort as 'asc' | 'desc' } : { field: null, order: null };
    debouncedRefetchForSort();
  }, [debouncedRefetchForSort, ignoreRestoreQueryEventsRef]);

  const onFilterChanged = useCallback(() => {
    if (ignoreRestoreQueryEventsRef.current) return;
    debouncedLoadFirstPage();
  }, [debouncedLoadFirstPage, ignoreRestoreQueryEventsRef]);

  const onGridReady = useCallback((params: any) => {
    augmentGridReady(params, (p) => {
      gridApiRef.current = p.api as GridApi;
      if (!getTableState(USERS_TABLE_ID)?.columnState?.length) {
        p.api.sizeColumnsToFit?.();
      }
    });
  }, [augmentGridReady]);

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    api.hideOverlay();
    if (!isLoading && rows.length === 0) api.showNoRowsOverlay();
  }, [rows, isLoading]);

  const handleEdit = useCallback(async (data: any) => {
    // Open modal immediately with stale row data + loading spinner
    setUserModal({ isOpen: true, mode: 'edit', userData: data, isUserDataLoading: true });
    try {
      // Fetch only this single user via the dedicated API (much cheaper than all-users refetch)
      const result = await triggerGetUserById(data.user_id).unwrap();
      const freshUserData = buildUserWithGroups(result.data);
      setUserModal({ isOpen: true, mode: 'edit', userData: freshUserData, isUserDataLoading: false });
    } catch {
      // Fallback: show modal with stale row data if the request fails
      setUserModal({ isOpen: true, mode: 'edit', userData: data, isUserDataLoading: false });
    }
  }, [triggerGetUserById]);
  const handleCreateUser = useCallback(() => setUserModal({ isOpen: true, mode: 'create', userData: null, isUserDataLoading: false }), []);
  const handleUserModalClose = useCallback(() => setUserModal({ isOpen: false, mode: 'create', userData: null, isUserDataLoading: false }), []);
  const handleDeleteModal = useCallback((data: any) => setDeleteModal({ isOpen: true, userData: data }), []);
  const handleDeleteModalClose = useCallback(() => setDeleteModal({ isOpen: false, userData: null }), []);
  const handleGroupFilter = useCallback((groupId: string) => setSelectedGroups(prev => prev.includes(groupId) ? [] : [groupId]), []);
  const handleClearFilters = useCallback(() => { setSelectedGroups([]); setSearchTerm(''); }, []);

  /** Clears column filters, sort, search and role chip; one reload instead of one per cleared filter */
  const handleResetFilters = useCallback(() => {
    const api = gridApiRef.current;
    if (api) {
      ignoreRestoreQueryEventsRef.current = true;
      api.setFilterModel(null);
      api.applyColumnState({ defaultState: { sort: null } });
      ignoreRestoreQueryEventsRef.current = false;
    }
    sortRef.current = { field: null, order: null };
    const searchOrRoleChanges = debouncedSearchTerm !== '' || selectedGroup !== null;
    setSelectedGroups([]);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    // The search/role effect reloads page 1 when either changes
    if (!searchOrRoleChanges) loadPage(1, targetPageRef.current.pageSize);
  }, [debouncedSearchTerm, selectedGroup, ignoreRestoreQueryEventsRef, loadPage]);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Name',
      field: 'name',
      flex: 2,
      minWidth: 170,
      cellRenderer: UserNameCellRenderer,
      ...textFilterConfig,
    },
    {
      headerName: 'Email',
      field: 'email',
      flex: 2,
      minWidth: 220,
      ...textFilterConfig,
    },
    {
      headerName: 'Organization Name',
      field: 'organization_name',
      flex: 2,
      minWidth: 190,
      ...textFilterConfig,
    },
    {
      headerName: 'Connection',
      field: 'connection',
      flex: 2,
      minWidth: 170,
      ...textFilterConfig,
    },
    {
      headerName: 'Inviter',
      field: 'inviter',
      flex: 2,
      minWidth: 170,
      ...textFilterConfig,
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 2,
      minWidth: 130,
      ...textFilterConfig,
    },
    {
      headerName: 'Assigned Roles',
      colId: 'assigned_groups',
      field: 'groups',
      // The query API cannot sort on roles
      sortable: false,
      flex: 3,
      minWidth: 300,
      cellRenderer: AssignedGroupsCellRenderer,
      ...textFilterConfig,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      filter: false,
      minWidth: 150,
      maxWidth: 150,
      pinned: 'right' as const,
      lockPinned: true,
      lockPosition: true,
      resizable: false,
      headerComponent: ActionsHeaderWithColumnChooser,
      headerComponentParams: { gridApiRef },
      cellRenderer: createActionsCellRenderer(
        handleEdit,
        handleDeleteModal,
        (userId: string) => deletingUsers.has(userId),
        !hasAccessManagement
      )
    }
  ], [handleEdit, handleDeleteModal, deletingUsers, hasAccessManagement]);

  const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    ...serverSideSortColDef,
  }), []);

  return (
    <>
      <div className="w-full py-6">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold">User Management</h2>
              <p className="text-default-500">Search users and filter by roles • Click role chips to filter users</p>
            </div>
            <CtyButton
              size="md"
              color="primary"
              variant="solid"
              onPress={handleCreateUser}
              startContent={<Icon icon="mdi:plus" width={16} height={16} />}
              isDisabled={!hasAccessManagement}
            >
              Add User
            </CtyButton>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Icon icon="mdi:magnify" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-default-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-default-600">Filter by role:</span>
              {(groupsData?.data?.groups || []).map((group: any) => (
                <button
                  key={group.group}
                  onClick={() => handleGroupFilter(group.group)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedGroups.includes(group.group)
                      ? 'bg-foreground text-background'
                      : 'bg-default-100 text-default-600 hover:bg-default-200'
                  }`}
                >
                  {group.group}
                </button>
              ))}
              {selectedGroups.length > 0 && (
                <CtyButton
                  size="sm"
                  variant="bordered"
                  onPress={handleClearFilters}
                  className="text-xs"
                >
                  Clear Filter
                </CtyButton>
              )}
            </div>
            <CtyButton id="fga-users-reset-filters-btn" className="rounded-md p-3 border-1 shrink-0" variant="bordered" size="sm" onPress={handleResetFilters}>
              Reset Filters
            </CtyButton>
          </div>
        </div>

        <div
          className="ag-theme-alpine relative"
          style={{
            height: "100%",
            width: "100%",
            '--ag-border-color': '#e0e0e0',
            '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)'
          } as React.CSSProperties}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/70 z-10">
              <CtySpinner size="md" />
            </div>
          )}
          <AgGridReact
            suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
            rowData={rows}
            components={serverSideFilterComponents}
            context={serverSideFilterContext}
            columnDefs={columnDefsWithSort}
            defaultColDef={defaultColDef}
            headerHeight={40}
            domLayout="autoHeight"
            onGridReady={onGridReady}
            onSortChanged={onSortChanged}
            onFilterChanged={onFilterChanged}
            getRowId={(p) => p?.data?.user_id}
            alwaysShowHorizontalScroll={true}
          />
          <ServerPaginationPanel
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={USERS_PAGE_SIZE_OPTIONS}
          />
        </div>
      </div>

      {/* Create/Edit User Modal */}
      {userModal.isOpen && (
        <CreateEditUserModal
          isOpen={userModal.isOpen}
          onOpenChange={handleUserModalClose}
          mode={userModal.mode}
          userData={userModal.userData}
          isUserDataLoading={userModal.isUserDataLoading}
          onSuccess={refetchUsers}
        />
      )}

      {/* Delete User Modal */}
      {deleteModal.isOpen && (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          onOpenChange={handleDeleteModalClose}
          type="user"
          data={{
            name: deleteModal.userData?.name || '',
            email: deleteModal.userData?.email || '',
            user_id: deleteModal.userData?.user_id,
            groups: deleteModal.userData?.groups || deleteModal.userData?.assigned_groups || [],
          }}
          onSuccess={refetchUsers}
        />
      )}
    </>
  );
};
